import React, { FunctionComponent, useState } from 'react'
import { Button, Heading, InputMoney, Text } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { mapLocaleToCurrency, TrackingEvent } from '../../model'
import { formatAmountForDisplay, getAmountError } from './helper'
import { setUserHasApplePaylaterCard, setUserHasInstoreCard, useAppDispatch, useAppRouter } from '../../state'
import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import { MAX_AMOUNT, MIN_AMOUNT, MIN_AMOUNT_LENGTH_TO_SCALE } from './constants'
import clsx from 'clsx'
import styles from './style.module.css'
import { useCreateCheckoutMutation } from '../../state/appleGateway/apis'
import { useApplePayEntryData } from '../../state/applePaylater/hooks'
import { ApplePaylaterFlowType } from '../../flow/apple-paylater/model'
import { getNextApplePaylaterRoute, parseHasInstoreCard } from '../../flow/apple-paylater/utils'
import { Route } from '../../config/router'
import { Country } from '@afterpay/types'
import { validateCreateApplePayCheckoutResponse } from '../../page/apple-paylater/entry/validation'

export const AdjustAmount: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { locale } = router
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [createCheckout, { isLoading, isSuccess }] = useCreateCheckoutMutation()
  const applePayEntryData = useApplePayEntryData()
  const dispatch = useAppDispatch()

  // Scale down container when input gets too long - generally only occurs when the input is in the thousands
  const shouldScale = amount.length >= MIN_AMOUNT_LENGTH_TO_SCALE && parseFloat(amount) >= 1000

  // On user input, clear the error if it exists
  const handleOnChange = (value: string): void => {
    setAmount(value)

    if (!isUndefinedOrEmpty(error)) {
      setError(undefined)
    }
  }

  const handleContinue = async (): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_ADJUST_AMOUNT_PAGE_CONTINUE_BUTTON)

    const validationError = getAmountError(amount, t, locale)
    setError(validationError)

    if (!isUndefinedOrEmpty(validationError)) {
      return
    }

    if (applePayEntryData?.flowType !== ApplePaylaterFlowType.InStore) {
      console.error('Invalid flow type:', applePayEntryData?.flowType)
      return
    }

    if (isNullOrUndefined(applePayEntryData?.sessionIdentifier) || isNullOrUndefined(applePayEntryData?.verifier)) {
      console.error('Missing session data:', applePayEntryData)
      return
    }

    try {
      const response = await createCheckout({
        sessionId: applePayEntryData.sessionIdentifier,
        verifier: applePayEntryData.verifier,
        checkoutAmount: {
          amount,
          currency: mapLocaleToCurrency(locale),
        },
      }).unwrap()

      const isValidCreateCheckoutResponse = validateCreateApplePayCheckoutResponse(response)

      if (!isValidCreateCheckoutResponse) {
        console.error('Create Apple Pay checkout response validation failed')
        router.replace(Route.TERMINAL_ERROR)
        return
      }

      const { redirectCheckoutUrl } = response
      const url = new URL(redirectCheckoutUrl)
      const queryParams: Record<string, string> = {}
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value
      })

      queryParams.country = Country.US

      const hasInstoreCard = parseHasInstoreCard(queryParams.userHasInstoreCard)
      dispatch(setUserHasApplePaylaterCard(queryParams.userHasApplePaylaterCard === 'true'))
      dispatch(setUserHasInstoreCard(hasInstoreCard))

      router.replaceQuery({ pathname: getNextApplePaylaterRoute(hasInstoreCard), query: queryParams })
    } catch (error) {
      console.error('Failed to create checkout:', error)
      router.replace(Route.TERMINAL_ERROR)
    }
  }

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.inputWrapper}>
        <Heading size='M' bold>
          {t('adjustAmount:heading')}
        </Heading>
        <div className={styles.centerInputMoneyContainer}>
          <div className={clsx(styles.inputMoneyContainer, shouldScale && styles.inputMoneyContainerScaled)} data-testid='input-money-scaled-container'>
            <InputMoney bold value={amount} onChange={handleOnChange} locale={locale} maxValue={2000} testNameSpace='adjust-amount' autoFocus />
          </div>
        </div>
        <Text size='XXS' color='Neutral 500'>
          {t('adjustAmount:amountRange', { minimum: formatAmountForDisplay(MIN_AMOUNT, locale), maximum: formatAmountForDisplay(MAX_AMOUNT, locale) })}
        </Text>
        {!isUndefinedOrEmpty(error) && (
          <Text size='XXS' color='Error'>
            {error}
          </Text>
        )}
      </div>
      <div className={styles.metaWrapper}>
        <Text size='XXS' color='Neutral 500'>
          {t('adjustAmount:meta')}
        </Text>
        <Button
          onClick={() => {
            void handleContinue()
          }}
          disabled={isLoading || isSuccess}
        >
          {t('common:continue')}
        </Button>
      </div>
    </div>
  )
}
